import { FC, ReactNode } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { OrderProductionStatusEnum, OrderStatusEnum } from '../../Services/api/orders/orders.interface';
import { normalizeOrderStatusColors, normalizeOrderStatusTitle } from '../../utils/normalizeOrderStatus';

interface IOrderStatusCell {
  status: OrderStatusEnum;
  productionStatus?: OrderProductionStatusEnum;
  errorMessage?: string;
}

const labelsOrderProductionStatus: Record<OrderProductionStatusEnum, string> = {
  [OrderProductionStatusEnum.STAND_BY]: 'Aguardando produção',
  [OrderProductionStatusEnum.PRODUCTION]: 'Em produção',
  [OrderProductionStatusEnum.HALF_FINISHED]: 'Parcialmente produzido',
  [OrderProductionStatusEnum.FINISHED]: 'Produzido',
};

const colorsOrderProductionStatus: Record<OrderProductionStatusEnum, { bgColor: string; textColor: string }> = {
  [OrderProductionStatusEnum.STAND_BY]: {
    bgColor: 'rgba(110, 197, 5, 0.16)',
    textColor: '#3d6b00',
  },
  [OrderProductionStatusEnum.PRODUCTION]: {
    bgColor: 'rgba(223, 148, 27, 0.18)',
    textColor: '#9a5b00',
  },
  [OrderProductionStatusEnum.HALF_FINISHED]: {
    bgColor: 'rgba(18, 120, 237, 0.14)',
    textColor: '#0d47a1',
  },
  [OrderProductionStatusEnum.FINISHED]: {
    bgColor: 'rgba(40, 85, 135, 0.14)',
    textColor: '#1a365d',
  },
};

interface StatusRowProps {
  label: string;
  value: string;
  bgColor: string;
  textColor: string;
  tooltip?: string;
}

const StatusRow: FC<StatusRowProps> = ({ label, value, bgColor, textColor, tooltip }) => {
  const content = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '72px minmax(0, 1fr)',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          px: 1,
          py: 0.625,
          borderRadius: 1,
          bgcolor: bgColor,
          border: '1px solid',
          borderColor: 'rgba(0,0,0,0.06)',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: textColor,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  if (!tooltip) return content;

  return (
    <Tooltip title={tooltip} placement="top-start" enterDelay={400}>
      <Box sx={{ width: '100%', minWidth: 0 }}>{content}</Box>
    </Tooltip>
  );
};

export const OrderStatusCell: FC<IOrderStatusCell> = ({
  status,
  productionStatus,
  errorMessage,
}) => {
  const orderColors = normalizeOrderStatusColors(status);
  const orderTitle = normalizeOrderStatusTitle(status) || status || 'Não informado';
  const productionTitle = productionStatus
    ? labelsOrderProductionStatus[productionStatus]
    : 'Não informado';
  const productionColors = productionStatus
    ? colorsOrderProductionStatus[productionStatus]
    : { bgColor: 'rgba(158, 158, 158, 0.12)', textColor: '#616161' };

  const shouldShowErrorTooltip = [
    OrderStatusEnum.DEAD,
    OrderStatusEnum.INVOICE_PROBLEM,
  ].includes(status);

  const orderRow = (
    <StatusRow
      label="Pedido"
      value={orderTitle}
      bgColor={orderColors.bgColor}
      textColor={orderColors.textColor}
      tooltip={shouldShowErrorTooltip ? undefined : `Pedido: ${orderTitle}`}
    />
  );

  let wrappedOrderRow: ReactNode = orderRow;
  if (shouldShowErrorTooltip) {
    wrappedOrderRow = (
      <Tooltip
        title={errorMessage || 'Motivo não informado'}
        placement="top-start"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'error.main',
              color: 'common.white',
              fontSize: '0.875rem',
              fontWeight: 600,
              '& .MuiTooltip-arrow': {
                color: 'error.main',
              },
            },
          },
        }}
      >
        <Box sx={{ width: '100%', cursor: 'help' }}>{orderRow}</Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        py: 0.75,
        px: 0.25,
      }}
    >
      {wrappedOrderRow}
      <StatusRow
        label="Produção"
        value={productionTitle}
        bgColor={productionColors.bgColor}
        textColor={productionColors.textColor}
        tooltip={`Produção: ${productionTitle}`}
      />
    </Box>
  );
};

export default OrderStatusCell;
